import '../styles/AddressAutocomplete.css';

import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import Address from '../models/Address';
import AddressService from '../services/AddressService';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ScriptService from '../services/ScriptService';
import TextField from '@mui/material/TextField';

interface AddressFormProps {
  value: string;
  hideSubFields: boolean;
  required: boolean;
  error: boolean;
  onChange: (address: Address) => void;
}

/**
 * Google Cloud Console (Bracken Cloud Live project)
 * - APIs & Services > Enable APIs & Services
 * - Library > Maps Javascript API
 * Enable the API, then under Additional APIs enable Places API
 */
const GOOGLE_MAPS_API_KEY = 'AIzaSyCxwSToLeMpbjrphvvdtFF4s13ATtsJkLI';

const AddressAutocomplete = (props: AddressFormProps) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete>();
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState(props.error);
  const autocompleteRef = useRef(null);
  const mapsApiUrl = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;

  useEffect(() => {
    ScriptService.load(mapsApiUrl, () => initAutocomplete());
  }, []);

  useEffect(() => {
    autocompleteRef.current.value = props.value;
  }, [props.value]);

  useEffect(() => {
    setError(props.error);
  }, [props.error]);

  useEffect(() => {
    if (autocomplete) {
      console.debug('Autocomplete initialised.');
      // * Avoid paying for data that you don't need by restricting the set of
      // * place fields that are returned to just the address components.
      autocomplete.setFields(['address_component']);
      // * When the user selects an address from the drop-down, populate the
      // * address fields in the form.
      autocomplete.addListener('place_changed', fillInAddress);
    }
  }, [autocomplete]);

  function initAutocomplete() {
    console.debug('Autocomplete initialising...');
    if (typeof google !== 'undefined') {
      // * Create the autocomplete object, restricting the search predictions to
      // * geographical location types.
      setAutocomplete(
        new google.maps.places.Autocomplete(autocompleteRef.current, {
          types: ['geocode']
        })
      );
    }
  }

  function fillInAddress() {
    const addressObject = autocomplete.getPlace();
    console.debug(addressObject);
    props.onChange(AddressService.convertToAddress(addressObject));
  }

  function handleMouseEnter() {
    if (!focused) {
      setFocused(true);
    }
  }

  function handleMouseLeave() {
    if (focused) {
      setFocused(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.value === '') {
      clearAddress();
    }
  }

  function clearAddress() {
    autocomplete.set('place', null);
    autocompleteRef.current.value = '';
  }

  return (
    <TextField
      label="Address"
      fullWidth
      variant="outlined"
      margin="dense"
      size="small"
      placeholder="Start typing your address"
      required={props.required}
      error={error}
      inputRef={autocompleteRef}
      InputProps={{
        endAdornment: focused && autocompleteRef.current.value && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={clearAddress}>
              <CancelIcon />
            </IconButton>
          </InputAdornment>
        )
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onChange={handleChange}
    />
  );
};

export default AddressAutocomplete;
